import { Button } from "../../../../../../components/common/Button";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsValue,
} from "../styles";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { encryptAlgorithm } from "../../../../../../utils/encryptionAlgorithm";
import {
  KlashaCNYQuotation,
  payoutRequestV2,
  setLoading,
} from "../../../../redux/slice";
import Loader from "../../../../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { sendOTP } from "../../../../../../api/usersAPI";
import WalletOTP from "../../../../components/SendFunds/components/WalletOTP";
import {
  DetailsLabel,
  DetailsSectionHeader,
  EditButton,
  SectionTitle,
} from "../../../../components/Withdrawal/styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatCurrencyAmount,
  getCountryName,
} from "../../../../../../utils/currency";
import dayjs from "dayjs";
import { formatValue } from "../../../../utils";
import {
  clearUserData,
  savedCNYData,
  setStep,
} from "../../../../redux/formDataSlice";
import { SuccessAlertContext } from "../../../../../../context/SuccessAlertContext";
import {
  PAYMENT_METHODS,
  paymentMethodConfigurations,
} from "../../../../const";
import { CNYProps, FormInputs } from "../../../../../../types/walletTypes";

const Review = ({ handlePrevious }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);
  const [verifyOTP, setVerifyOTP] = useState(false);
  const { currencyCode } = useParams();
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});
  const accountUser = useAppSelector(
    (state) => state.users.userObj?.accountUser,
  );
  const isB2B = getSavedData?.paymentMethod === "bank_account_business";

  const senderFullName = isB2B
    ? `${getSavedData.senderBusinessRegisteredName}`
    : `${getSavedData?.senderFirstName} ${getSavedData?.senderLastName}`;
  const beneficiaryFullName = isB2B
    ? `${getSavedData?.receiverBusinessRegisteredName}`
    : `${getSavedData.receiverFirstName} ${getSavedData.receiverLastName}`;
  const totalAmount = getSavedData.totalAmount;

  const currentDate = dayjs().format("YYYY-MM-DD");

  const senderCountry = getCountryName(getSavedData?.senderAddress?.countryCode);

  const { onShowAlert: onShowSuccessAlert } =
    useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;

  /* Note: Needed to update the quote when an error occurs. Feel free to abstract this and what's in the amount step into a utils */
  const getKlashaQuotation = useCallback(async () => {
    const config = paymentMethodConfigurations[getSavedData?.paymentMethod] || {
      serviceCode: "",
      service: "",
      fundSource: "",
    };

    const data = {
      sourceCurrency: "USD",
      destinationCurrency: "CNY",
      destinationAmount: getSavedData?.amount,
      ...config,
    };

    const encryptedData = encryptAlgorithm(data, keys?.data?.encryptionKey);
    const payload = { message: encryptedData };
    const token = keys?.data?.publicKey;

    const action = await dispatch(
      KlashaCNYQuotation({ message: payload, token }),
    );

    if (KlashaCNYQuotation.fulfilled.match(action)) {
      const { fxRate, fee, sourceAmount, id, reference } = action.payload;
      dispatch(
        savedCNYData({
          quotationId: id,
          reference: reference,
          rate: fxRate,
          fee: fee,
          sourceAmount: sourceAmount,
        }),
      );

      onShowSuccessAlert("Quotation updated successfully");
    } else if (KlashaCNYQuotation.rejected.match(action)) {
      onShowErrorAlert("", action.payload as string);
    }
  }, []);

  const buildBasePayload = (data: FormInputs) => ({
    bankCode: data.bankCode,
    bankName: data.bankName,
    creditAccountCountry: "CN",
    quotationId: data.quotationId,
    receiverFirstName: data.receiverFirstName,
    receiverLastName: data.receiverLastName,
    requestId: data.reference,
    senderBusinessRegisteredName: data.senderBusinessRegisteredName,
    senderBusinessAddress: data.senderAddress,
  });

  const buildUnionPayPayload = (data: FormInputs) => {
    const { senderBusinessAddress, ...basePayload } = buildBasePayload(data);

    return {
      ...basePayload,
      cardHolderName: `${data.accountName.firstName} ${data.accountName.lastName}`,
      cardNumber: data.cardNumber,
      purpose: data.purpose,
      senderFirstName: data.senderFirstName,
      senderLastName: data.senderLastName,
      senderIdNumber: data.senderIdNumber,
      senderIdType: data.senderIdType,
      senderNationality: "CN",
      senderAddress: data.senderAddress,
    };
  };

  const buildBankAccountIndividualPayload = (data: FormInputs) => ({
    ...buildBasePayload(data),
    accountName: `${data.accountName.firstName} ${data.accountName.lastName}`,
    accountNumber: data.accountNumber,
    accountType: data.accountType.toUpperCase(),
    purpose: "SALARY",
    receiverIdNumber: data.receiverIdNumber,
    receiverIdType: data.receiverIdType,
    receiverMobileNumber: data.receiverMobileNumber,
    receiverRelationship: "EMPLOYEE",
    senderNationality: data.senderAddress?.countryCode,
    senderBusinessIdNumber: data.senderIdNumber,
    senderBusinessIdType: data.senderIdType,
  });

  const buildBankAccountBusinessPayload = (data: FormInputs) => ({
    ...buildBasePayload(data),
    accountName: data.bankAccountName,
    accountNumber: data.accountNumber,
    accountType: "COMPANY",
    purpose: data.purpose,
    receiverBusinessIdNumber: data.receiverBusinessIdNumber,
    receiverBusinessIdType: data.receiverBusinessIdType,
    receiverBusinessRegisteredName: data.receiverBusinessRegisteredName,
    receiverBusinessMobileNumber: data.receiverMobileNumber,
    receiverBusinessAddress: data.receiverBusinessAddress,
    document: data.document,
    website: data.website,
  });

  const handlePayout = async () => {
    let updatedPayload;

    switch (getSavedData.paymentMethod) {
      case PAYMENT_METHODS.unionpay:
        updatedPayload = buildUnionPayPayload(getSavedData);
        break;
      case PAYMENT_METHODS.bank_account_individual:
        updatedPayload = buildBankAccountIndividualPayload(getSavedData);
        break;

      case PAYMENT_METHODS.bank_account_business:
        updatedPayload = buildBankAccountBusinessPayload(getSavedData);
        break;
    }

    const encryptedData = encryptAlgorithm(
      updatedPayload,
      keys?.data?.encryptionKey,
    );
    const finalPayload = { message: encryptedData };

    const action = await dispatch(
      payoutRequestV2({ data: finalPayload, token: publicKey }),
    );

    if (payoutRequestV2.fulfilled.match(action)) {
      onShowSuccessAlert("Payout successful");
      dispatch(clearUserData());
      navigate("/wallets");
    } else if (payoutRequestV2.rejected.match(action)) {
      onShowErrorAlert("", action.payload as string);
      await getKlashaQuotation();
    }
  };

  const handleSendOTP = async (user: { email: string }) => {
    try {
      dispatch(setLoading(true));
      await sendOTP(user);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert("", error?.response as string);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePrevStep = (index: number) => {
    dispatch(setStep(index));
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP setVerifyOTP={setVerifyOTP} makePayment={handlePayout} />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Amount sent</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(currencyCode, getSavedData?.amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.rate)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.fee)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.paymentPurpose ?? "Salary")}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Sender details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(1)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Sender type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.senderType ?? "Individual")}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender name</DetailsLabel>
            <DetailsValue>{senderFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender country</DetailsLabel>
            <DetailsValue>{senderCountry}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary's details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(2)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>
        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.receiverAccountType)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{beneficiaryFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Phone number</DetailsLabel>
            <DetailsValue>{getSavedData?.receiverMobileNumber}</DetailsValue>
          </DetailsRow>
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={() => handleSendOTP(accountUser?.email)}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default Review;
